import { addCalendarDays, mondayOf, type ViennaToday } from '@pulsify/shared'
import {
  type FullMenuDto,
  type FullMenuInput,
  type FullMenuSection,
  fullMenuInputSchema,
  type IsoWeekday,
  type ListingMenuKind,
  type LunchMenuDay,
  type LunchMenuDto,
  type LunchMenuInput,
  lunchMenuInputSchema,
  MENU_MAX_SECTIONS,
  type MenuDiet,
  type MenuDish,
  menuDishSchema,
  type ScannedMenu,
} from '@pulsify/shared/dto'

export const ISO_WEEKDAYS: readonly IsoWeekday[] = [1, 2, 3, 4, 5, 6, 7]
const DIET_ORDER: readonly MenuDiet[] = ['vegetarian', 'vegan']

export interface DishDraft {
  key: string
  name: string
  description: string
  /** What the owner typed; read with `parsePriceInput`. */
  price: string
  diets: MenuDiet[]
}

interface LunchDayDraft {
  weekday: IsoWeekday
  dishes: DishDraft[]
}

export interface LunchMenuDraft {
  kind: 'lunch'
  title: string
  note: string
  weekMode: 'week' | 'every_week'
  /** The Monday of the week; kept while the board is the same every week, so switching back restores it. */
  weekOf: string
  days: LunchDayDraft[]
}

interface SectionDraft {
  key: string
  title: string
  dishes: DishDraft[]
}

export interface FullMenuDraft {
  kind: 'full'
  title: string
  note: string
  sections: SectionDraft[]
}

export type MenuDraft = LunchMenuDraft | FullMenuDraft

export type MenuDraftIssue =
  | 'invalid_price'
  | 'dish_without_name'
  | 'section_without_title'
  | 'no_dishes'
  | 'invalid'

type MenuInputResult<T> = { ok: true; input: T } | { ok: false; issue: MenuDraftIssue }

export const INVALID_PRICE = 'invalid'

const CURRENCY = /€|eur(?:o)?/gi
const NO_CENTS = /[.,][-–-]+$/
const PLAIN = /^(\d{1,6})(?:[.,](\d{1,2}))?$/
const GROUPED_COMMA_DECIMALS = /^(\d{1,3}(?:\.\d{3})+),(\d{1,2})$/
const GROUPED_DOT_DECIMALS = /^(\d{1,3}(?:,\d{3})+)\.(\d{1,2})$/

/**
 * Reads a typed price into cents: "8,50", "8.50 €", "8,-" and "1.250,00" all work and an empty
 * field means no price. Anything else ("8,50 / 9,50", "-3", "12,345") is `INVALID_PRICE`.
 */
export function parsePriceInput(text: string): number | null | typeof INVALID_PRICE {
  if (text.trim() === '') return null
  const cleaned = text.replace(CURRENCY, '').trim().replace(NO_CENTS, '')
  const match =
    PLAIN.exec(cleaned) ??
    GROUPED_COMMA_DECIMALS.exec(cleaned) ??
    GROUPED_DOT_DECIMALS.exec(cleaned)
  if (!match) return INVALID_PRICE
  const euros = Number(match[1].replace(/[.,]/g, ''))
  const cents = Number((match[2] ?? '').padEnd(2, '0'))
  return euros * 100 + cents
}

const priceCentsSchema = menuDishSchema.shape.priceCents

/** False while the price field cannot be read or holds more than a menu allows. */
export function isPriceInputValid(text: string): boolean {
  const cents = parsePriceInput(text)
  return cents !== INVALID_PRICE && priceCentsSchema.safeParse(cents).success
}

/** Cents the way the owner types them: "8,50" in German, "8.50" in English. */
export function formatPriceInput(cents: number | null, locale: string): string {
  if (cents === null) return ''
  const text = (cents / 100).toFixed(2)
  return locale.startsWith('de') ? text.replace('.', ',') : text
}

let lastKey = 0

function nextKey(prefix: string): string {
  lastKey += 1
  return `${prefix}-${lastKey}`
}

function newDish(): DishDraft {
  return { key: nextKey('dish'), name: '', description: '', price: '', diets: [] }
}

function draftDish(dish: MenuDish, locale: string): DishDraft {
  return {
    key: nextKey('dish'),
    name: dish.name,
    description: dish.description ?? '',
    price: formatPriceInput(dish.priceCents, locale),
    diets: [...dish.diets],
  }
}

function newSection(title = '', dishes: DishDraft[] = [newDish()]): SectionDraft {
  return { key: nextKey('section'), title, dishes }
}

function draftDays(days: readonly LunchMenuDay[], locale: string): LunchDayDraft[] {
  return [...days]
    .sort((a, b) => a.weekday - b.weekday)
    .map((day) => ({
      weekday: day.weekday,
      dishes: day.dishes.map((dish) => draftDish(dish, locale)),
    }))
}

function draftSections(sections: readonly FullMenuSection[], locale: string): SectionDraft[] {
  return sections.map((section) =>
    newSection(
      section.title,
      section.dishes.map((dish) => draftDish(dish, locale)),
    ),
  )
}

function firstRow(weekOf: string, today: ViennaToday): LunchDayDraft[] {
  return [{ weekday: weekOf === mondayOf(today) ? today.weekday : 1, dishes: [newDish()] }]
}

/** What "Selbst eintragen" opens: this week with a row on today, or one empty section. */
export function emptyDraft(kind: ListingMenuKind, today: ViennaToday): MenuDraft {
  if (kind === 'full') return { kind: 'full', title: '', note: '', sections: [newSection()] }
  return {
    kind: 'lunch',
    title: '',
    note: '',
    weekMode: 'week',
    weekOf: mondayOf(today),
    days: firstRow(mondayOf(today), today),
  }
}

/**
 * The editor after a reading; a reading that found nothing still gets one row to type into, and a
 * saved board that is the same every week stays that way.
 */
export function fromScannedMenu(
  menu: ScannedMenu,
  today: ViennaToday,
  locale: string,
  savedLunch: LunchMenuDto | null = null,
): MenuDraft {
  if (menu.kind === 'full') {
    return {
      kind: 'full',
      title: menu.title ?? '',
      note: menu.note ?? '',
      sections: menu.sections.length > 0 ? draftSections(menu.sections, locale) : [newSection()],
    }
  }
  const days = draftDays(menu.days, locale)
  return {
    kind: 'lunch',
    title: menu.title ?? '',
    note: menu.note ?? '',
    weekMode: savedLunch && savedLunch.weekOf === null ? 'every_week' : 'week',
    weekOf: menu.weekOf,
    days: days.length > 0 ? days : firstRow(menu.weekOf, today),
  }
}

export function fromSavedMenu(
  menu: LunchMenuDto | FullMenuDto,
  today: ViennaToday,
  locale: string,
): MenuDraft {
  if (menu.kind === 'full') {
    return {
      kind: 'full',
      title: menu.title ?? '',
      note: menu.note ?? '',
      sections: draftSections(menu.sections, locale),
    }
  }
  return {
    kind: 'lunch',
    title: menu.title ?? '',
    note: menu.note ?? '',
    weekMode: menu.weekOf ? 'week' : 'every_week',
    weekOf: menu.weekOf ?? mondayOf(today),
    days: draftDays(menu.days, locale),
  }
}

export function countScannedDishes(menu: ScannedMenu): number {
  const lists: readonly { dishes: readonly MenuDish[] }[] =
    menu.kind === 'lunch' ? menu.days : menu.sections
  return lists.reduce((sum, list) => sum + list.dishes.length, 0)
}

export function addLunchDay(draft: LunchMenuDraft, weekday: IsoWeekday): LunchMenuDraft {
  if (draft.days.some((day) => day.weekday === weekday)) return draft
  const days = [...draft.days, { weekday, dishes: [newDish()] }]
  return { ...draft, days: days.sort((a, b) => a.weekday - b.weekday) }
}

export function removeLunchDay(draft: LunchMenuDraft, weekday: IsoWeekday): LunchMenuDraft {
  return { ...draft, days: draft.days.filter((day) => day.weekday !== weekday) }
}

export function updateLunchDay(
  draft: LunchMenuDraft,
  weekday: IsoWeekday,
  update: (dishes: DishDraft[]) => DishDraft[],
): LunchMenuDraft {
  return {
    ...draft,
    days: draft.days.map((day) =>
      day.weekday === weekday ? { ...day, dishes: update(day.dishes) } : day,
    ),
  }
}

/** The weekday tab on show: the picked one while it exists, else the first day. */
export function shownLunchDay(draft: LunchMenuDraft, picked: IsoWeekday | null): IsoWeekday | null {
  if (picked !== null && draft.days.some((day) => day.weekday === picked)) return picked
  return draft.days[0]?.weekday ?? null
}

export function shiftLunchWeek(draft: LunchMenuDraft, weeks: number): LunchMenuDraft {
  return { ...draft, weekOf: addCalendarDays(draft.weekOf, weeks * 7) }
}

export function addSection(draft: FullMenuDraft): FullMenuDraft {
  if (draft.sections.length >= MENU_MAX_SECTIONS) return draft
  return { ...draft, sections: [...draft.sections, newSection()] }
}

export function removeSection(draft: FullMenuDraft, key: string): FullMenuDraft {
  return { ...draft, sections: draft.sections.filter((section) => section.key !== key) }
}

export function renameSection(draft: FullMenuDraft, key: string, title: string): FullMenuDraft {
  return {
    ...draft,
    sections: draft.sections.map((section) =>
      section.key === key ? { ...section, title } : section,
    ),
  }
}

export function moveSection(draft: FullMenuDraft, key: string, direction: -1 | 1): FullMenuDraft {
  const from = draft.sections.findIndex((section) => section.key === key)
  const to = from + direction
  if (from === -1 || to < 0 || to >= draft.sections.length) return draft
  const sections = [...draft.sections]
  const [moved] = sections.splice(from, 1)
  sections.splice(to, 0, moved)
  return { ...draft, sections }
}

export function updateSectionDishes(
  draft: FullMenuDraft,
  key: string,
  update: (dishes: DishDraft[]) => DishDraft[],
): FullMenuDraft {
  return {
    ...draft,
    sections: draft.sections.map((section) =>
      section.key === key ? { ...section, dishes: update(section.dishes) } : section,
    ),
  }
}

/** Every dish row of a Speisekarte, for the limit on the whole menu. */
export function fullMenuRowCount(draft: FullMenuDraft): number {
  return draft.sections.reduce((sum, section) => sum + section.dishes.length, 0)
}

export function appendDish(dishes: DishDraft[], limit: number): DishDraft[] {
  return dishes.length >= limit ? dishes : [...dishes, newDish()]
}

export function removeDish(dishes: DishDraft[], key: string): DishDraft[] {
  return dishes.filter((dish) => dish.key !== key)
}

export function patchDish(
  dishes: DishDraft[],
  key: string,
  patch: Partial<Omit<DishDraft, 'key'>>,
): DishDraft[] {
  return dishes.map((dish) => (dish.key === key ? { ...dish, ...patch } : dish))
}

export function toggleDiet(diets: readonly MenuDiet[], diet: MenuDiet): MenuDiet[] {
  const next = diets.includes(diet) ? diets.filter((entry) => entry !== diet) : [...diets, diet]
  return DIET_ORDER.filter((entry) => next.includes(entry))
}

const collapse = (text: string) => text.replace(/\s+/g, ' ').trim()

type DishReading =
  | { kind: 'empty' }
  | { kind: 'dish'; dish: MenuDish }
  | { kind: 'issue'; issue: MenuDraftIssue }

function readDish(draft: DishDraft): DishReading {
  const name = collapse(draft.name)
  const description = collapse(draft.description)
  if (!name && !description && draft.price.trim() === '') return { kind: 'empty' }
  const priceCents = parsePriceInput(draft.price)
  if (priceCents === INVALID_PRICE || !priceCentsSchema.safeParse(priceCents).success) {
    return { kind: 'issue', issue: 'invalid_price' }
  }
  if (!name) return { kind: 'issue', issue: 'dish_without_name' }
  return {
    kind: 'dish',
    dish: { name, description: description || null, priceCents, diets: [...draft.diets] },
  }
}

function readDishes(
  drafts: readonly DishDraft[],
): { ok: true; dishes: MenuDish[] } | { ok: false; issue: MenuDraftIssue } {
  const dishes: MenuDish[] = []
  for (const draft of drafts) {
    const reading = readDish(draft)
    if (reading.kind === 'issue') return { ok: false, issue: reading.issue }
    if (reading.kind === 'dish') dishes.push(reading.dish)
  }
  return { ok: true, dishes }
}

/** The Mittagsmenü to save: empty rows and days dropped, checked with the shared schema. */
export function toLunchMenuInput(draft: LunchMenuDraft): MenuInputResult<LunchMenuInput> {
  const days: LunchMenuDay[] = []
  for (const day of draft.days) {
    const reading = readDishes(day.dishes)
    if (!reading.ok) return reading
    if (reading.dishes.length > 0) days.push({ weekday: day.weekday, dishes: reading.dishes })
  }
  if (days.length === 0) return { ok: false, issue: 'no_dishes' }
  const parsed = lunchMenuInputSchema.safeParse({
    title: collapse(draft.title) || null,
    note: draft.note.trim() || null,
    weekOf: draft.weekMode === 'week' ? draft.weekOf : null,
    days,
  })
  return parsed.success ? { ok: true, input: parsed.data } : { ok: false, issue: 'invalid' }
}

/** The Speisekarte to save: empty rows and sections dropped, checked with the shared schema. */
export function toFullMenuInput(draft: FullMenuDraft): MenuInputResult<FullMenuInput> {
  const sections: FullMenuSection[] = []
  for (const section of draft.sections) {
    const reading = readDishes(section.dishes)
    if (!reading.ok) return reading
    if (reading.dishes.length === 0) continue
    const title = collapse(section.title)
    if (!title) return { ok: false, issue: 'section_without_title' }
    sections.push({ title, dishes: reading.dishes })
  }
  if (sections.length === 0) return { ok: false, issue: 'no_dishes' }
  const parsed = fullMenuInputSchema.safeParse({
    title: collapse(draft.title) || null,
    note: draft.note.trim() || null,
    sections,
  })
  return parsed.success ? { ok: true, input: parsed.data } : { ok: false, issue: 'invalid' }
}

/** Why the draft cannot be published yet, or null when it can. */
export function draftIssue(draft: MenuDraft): MenuDraftIssue | null {
  const result = draft.kind === 'lunch' ? toLunchMenuInput(draft) : toFullMenuInput(draft)
  return result.ok ? null : result.issue
}
